import { Button, Divider, TextField, Typography } from '@mui/material';
import { Box } from '@mui/system';
import {
  RichTreeView,
  treeItemClasses,
  TreeViewBaseItem,
  useTreeViewApiRef,
} from '@mui/x-tree-view';
import { FieldTemplate, FormTemplateType } from 'api/models';
import usePatientFieldsMap from 'hooks/usePatientFieldsMap';
import { useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

function getItemDescendantsIds(item?: TreeViewBaseItem) {
  const ids: string[] = [];
  item?.children?.forEach(child => {
    ids.push(child.id);
    ids.push(...getItemDescendantsIds(child));
  });

  return ids;
}

interface FieldTreeProps {
  fields?: FieldTemplate[];
  patientFields?: string[];
  formTemplates?: FormTemplateType[];
  onChange: (fields: FieldTemplate[]) => void;
}

export default function FieldTree({
  fields,
  patientFields,
  formTemplates,
  onChange,
}: FieldTreeProps) {
  const [selectedItems, setSelectedItems] = useState<string[]>([]);
  const [query, setQuery] = useState('');
  const { t } = useTranslation();
  const patientFieldMap = usePatientFieldsMap();
  const basePatientFields =
    ['idTaj', 'idOther', ...(patientFields ?? [])].map(
      (patientField: string) => {
        if (!patientFieldMap.has(patientField)) {
          throw new Error(
            `Patient field ${patientField} not found in patientFieldMap, probably new fields were added in the API`,
          );
        }

        return patientFieldMap.get(patientField)!;
      },
    ) ?? [];

  console.log('basePatientFields', patientFields);

  const formMap = new Map<string, string>();
  formTemplates?.forEach(formTemplateType => {
    formMap.set(formTemplateType.id, formTemplateType.name);
  });

  const fieldMap = [...(fields ?? []), ...basePatientFields]?.reduce(
    (map, f) => {
      map.set(f.id, f);
      return map;
    },
    new Map<string, FieldTemplate>(),
  );

  const fieldGroups =
    fields
      ?.filter(f => formMap.has(f.formTemplateTypeId ?? ''))
      .reduce<Record<string, FieldTemplate[]>>((res, field) => {
        const id = `<field_template_parent>:${field.formTemplateTypeId}`;
        if (res[id]) {
          res[id].push(field);
        } else {
          res[id] = [field];
        }
        return res;
      }, {}) ?? {};

  const items = [
    {
      id: '<field_template_parent>:patient.data',
      label: t('export.patientData'),
      children: basePatientFields.map(p => {
        return {
          id: p.id,
          label: p.fieldAttributes.label,
        };
      }),
    },
  ];

  for (const [key, value] of Object.entries(fieldGroups)) {
    items.push({
      id: key,
      label: formMap.get(key.replaceAll('<field_template_parent>:', '')) ?? '',
      children: value.map(f => ({
        id: f.id,
        label: `${f.fieldAttributes.label} - ${f.alias}`,
      })),
    });
  }

  const toggledItemRef = useRef<{ [itemId: string]: boolean }>({});
  const apiRef = useTreeViewApiRef();

  const handleItemSelectionToggle = (
    event: React.SyntheticEvent,
    itemId: string,
    isSelected: boolean,
  ) => {
    toggledItemRef.current[itemId] = isSelected;
  };

  const handleSelectedItemsChange = (
    _event: React.SyntheticEvent,
    newSelectedItems: string[],
  ) => {
    const itemsToSelect: string[] = [];
    const itemsToUnSelect: { [itemId: string]: boolean } = {};
    Object.entries(toggledItemRef.current).forEach(([itemId, isSelected]) => {
      const item = apiRef.current?.getItem(itemId);
      if (isSelected) {
        itemsToSelect.push(...getItemDescendantsIds(item));
      } else {
        getItemDescendantsIds(item).forEach(descendantId => {
          itemsToUnSelect[descendantId] = true;
        });
      }
    });

    const newSelectedItemsWithChildren = Array.from(
      new Set(
        [...newSelectedItems, ...itemsToSelect].filter(
          itemId => !itemsToUnSelect[itemId],
        ),
      ),
    );
    setSelectedItems(newSelectedItemsWithChildren);
    onChange(newSelectedItemsWithChildren.map(item => fieldMap.get(item)!));
    toggledItemRef.current = {};
  };

  return (
    <Box mt={2}>
      <Divider />

      <Typography variant="h5" mt={2} mb={1}>
        {t('export.fieldSelectorTitle')}
      </Typography>

      <Box display={'flex'} flexDirection={'row'} alignItems={'center'}>
        <TextField
          size="small"
          label={t('general.search')}
          sx={{ m: 2 }}
          value={query}
          onChange={e => setQuery(e.target.value)}
        />
        <Button
          sx={{ mb: 1 }}
          onClick={() => {
            const ids = items.flatMap(i => {
              const childIds = i.children.map(c => c.id);
              return [i.id, ...childIds];
            });
            if (selectedItems.length > 0) {
              setSelectedItems([]);
              onChange([]);
            } else {
              setSelectedItems(ids);
              onChange(ids.map(i => fieldMap.get(i)!));
            }
          }}
        >
          {selectedItems.length !== 0
            ? t('dialogs.addField.deSelectAll')
            : t('dialogs.addField.selectAll')}
        </Button>
      </Box>
      <RichTreeView
        apiRef={apiRef}
        items={items}
        checkboxSelection
        multiSelect
        selectedItems={selectedItems}
        onItemSelectionToggle={handleItemSelectionToggle}
        onSelectedItemsChange={handleSelectedItemsChange}
        sx={{
          [`& .${treeItemClasses.content}`]: {
            mt: '2px',
          },
        }}
      />
    </Box>
  );
}
